/**
 * Shared Recharge SDK bootstrap for theme scripts.
 *
 * Exposes `window.etRechargeSdk.ready()` so feature scripts can share one
 * client load/init path instead of duplicating SDK setup.
 */
(function bootstrapSharedRechargeSdk() {
  if (window.__ET_RECHARGE_SDK_BOOTSTRAP_LOADED__) return;
  window.__ET_RECHARGE_SDK_BOOTSTRAP_LOADED__ = true;

  const DEFAULTS = Object.freeze({
    // Fallback only: layout/theme.liquid passes rechargeClientScriptUrl and
    // loads that same URL itself on overview landings, so bump it there.
    rechargeClientScriptUrl:
      'https://static.rechargecdn.com/assets/storefront/recharge-client-1.70.0.min.js',
    rechargeClientLoadTimeoutMs: 10 * 1000,
    appName: 'everytable-recharge-theme',
    appVersion: '1.0.0',
  });

  const state = {
    loadPromise: null,
    readyPromise: null,
  };

  function getRuntimeConfig() {
    const config =
      window.etRechargeSdkConfig && typeof window.etRechargeSdkConfig === 'object'
        ? window.etRechargeSdkConfig
        : {};

    const timeoutCandidate = Number.parseInt(
      String(config.rechargeClientLoadTimeoutMs || ''),
      10
    );
    const timeoutMs = Number.isFinite(timeoutCandidate) && timeoutCandidate > 0
      ? timeoutCandidate
      : DEFAULTS.rechargeClientLoadTimeoutMs;

    return {
      rechargeClientScriptUrl: String(
        config.rechargeClientScriptUrl || DEFAULTS.rechargeClientScriptUrl
      ).trim(),
      rechargeClientLoadTimeoutMs: timeoutMs,
      appName: String(config.appName || DEFAULTS.appName).trim(),
      appVersion: String(config.appVersion || DEFAULTS.appVersion).trim(),
      storeIdentifier: String(
        config.storeIdentifier || window.Shopify?.shop || ''
      ).trim(),
      storefrontAccessToken: resolveRechargeStorefrontAccessToken(config),
    };
  }

  function resolveRechargeClient() {
    const fromWindow =
      window.recharge && typeof window.recharge === 'object'
        ? window.recharge
        : null;
    const fromGlobal =
      typeof globalThis.recharge !== 'undefined' &&
      globalThis.recharge &&
      typeof globalThis.recharge === 'object'
        ? globalThis.recharge
        : null;

    const resolved = fromWindow || fromGlobal || null;
    if (!window.recharge && resolved) {
      window.recharge = resolved;
    }
    return resolved;
  }

  function resolveRechargeStorefrontAccessToken(config) {
    const globalApiToken =
      typeof apiToken !== 'undefined' ? String(apiToken || '').trim() : '';
    const candidates = [
      config.storefrontAccessToken,
      config.rechargeStorefrontAccessToken,
      globalApiToken,
      window.apiToken,
      window.RECHARGE_STOREFRONT_ACCESS_TOKEN,
    ];

    for (const candidate of candidates) {
      const token = String(candidate || '').trim();
      if (!token) continue;
      if (token.toLowerCase().startsWith('strfnt_')) return token;
    }

    return null;
  }

  function getMethod(pathSegments) {
    const segments = Array.isArray(pathSegments) ? pathSegments : [];
    if (!segments.length) {
      throw new Error('Recharge method path must be a non-empty array.');
    }

    const rechargeClient = resolveRechargeClient();
    let cursor = rechargeClient;
    let parent = null;
    for (const segment of segments) {
      if (!cursor || typeof cursor !== 'object') {
        throw new Error(
          `Recharge client is not available at "${segments.join('.')}"`
        );
      }
      parent = cursor;
      cursor = cursor[segment];
    }
    if (typeof cursor !== 'function') {
      throw new Error(
        `Recharge method "${segments.join('.')}" is not a function`
      );
    }

    if (parent && typeof cursor.bind === 'function') {
      return cursor.bind(parent);
    }

    return cursor;
  }

  async function ensureRechargeClientLoaded() {
    const existingClient = resolveRechargeClient();
    if (existingClient) return existingClient;

    if (state.loadPromise) {
      await state.loadPromise;
      return resolveRechargeClient();
    }

    state.loadPromise = new Promise((resolve, reject) => {
      const runtimeConfig = getRuntimeConfig();
      const existingScript = document.querySelector(
        'script[data-et-recharge-client="true"]'
      );

      const onReady = () => {
        const client = resolveRechargeClient();
        if (!client) {
          reject(
            new Error(
              'Recharge client script loaded but global `recharge` object was not found.'
            )
          );
          return;
        }
        resolve(client);
      };

      const onError = () => {
        reject(
          new Error(
            `Failed to load Recharge client script: ${runtimeConfig.rechargeClientScriptUrl}`
          )
        );
      };

      if (existingScript) {
        if (resolveRechargeClient()) {
          resolve(resolveRechargeClient());
          return;
        }
        existingScript.addEventListener('load', onReady, { once: true });
        existingScript.addEventListener('error', onError, { once: true });
      } else {
        const script = document.createElement('script');
        script.src = runtimeConfig.rechargeClientScriptUrl;
        script.async = true;
        script.defer = true;
        script.dataset.etRechargeClient = 'true';
        script.addEventListener('load', onReady, { once: true });
        script.addEventListener('error', onError, { once: true });
        document.head.appendChild(script);
      }

      window.setTimeout(() => {
        if (!resolveRechargeClient()) {
          reject(
            new Error(
              `Timed out loading Recharge client script after ${runtimeConfig.rechargeClientLoadTimeoutMs}ms`
            )
          );
        }
      }, runtimeConfig.rechargeClientLoadTimeoutMs);
    });

    try {
      await state.loadPromise;
    } catch (error) {
      state.loadPromise = null;
      throw error;
    }

    return resolveRechargeClient();
  }

  async function ready() {
    if (state.readyPromise) return state.readyPromise;

    state.readyPromise = (async () => {
      await ensureRechargeClientLoaded();
      const rechargeClient = resolveRechargeClient();
      if (!rechargeClient) {
        throw new Error('Recharge client unavailable after load.');
      }

      const runtimeConfig = getRuntimeConfig();
      if (
        runtimeConfig.storefrontAccessToken &&
        typeof rechargeClient.init === 'function'
      ) {
        const initOptions = {
          storefrontAccessToken: runtimeConfig.storefrontAccessToken,
          appName: runtimeConfig.appName,
          appVersion: runtimeConfig.appVersion,
        };

        if (runtimeConfig.storeIdentifier) {
          initOptions.storeIdentifier = runtimeConfig.storeIdentifier;
        }

        rechargeClient.init(initOptions);
      }

      return rechargeClient;
    })();

    try {
      await state.readyPromise;
    } catch (error) {
      state.readyPromise = null;
      throw error;
    }

    return state.readyPromise;
  }

  window.etRechargeSdk = Object.freeze({
    ready,
    ensureRechargeClientLoaded,
    resolveRechargeClient,
    getMethod,
    resolveRechargeStorefrontAccessToken: () =>
      resolveRechargeStorefrontAccessToken(getRuntimeConfig()),
  });

  ready().catch((error) => {
    console.warn('[recharge-sdk-bootstrap] Initial SDK bootstrap failed.', error);
  });
})();
